import { useEffect, useState } from "react";
import { AuthContextProps, useAuth } from "react-oidc-context";
import { BFF_ENABLED, bffLogin, bffLogout, fetchBffSession } from "app/config/bff";
import { DESIGN_PREVIEW, PREVIEW_USER_ID } from "app/config/designPreview";

/**
 * 로그인 상태를 한 곳에서 읽는다.
 *
 * ── 왜 필요한가
 *
 * 화면 여러 곳이 {@code useAuth()} 를 직접 써서 토큰 유무로 로그인을 판단하고 있었다.
 * BFF 를 켜면 화면에 토큰이 없으므로 그 판단이 전부 어긋난다.
 *
 * 이 훅이 두 방식을 같은 모양으로 덮는다. 부르는 쪽은 어느 방식인지 몰라도 된다.
 *
 * ── 훅 규칙
 *
 * {@code useAuth()} 를 조건 없이 호출한다. 방식에 따라 호출을 건너뛰면 렌더마다 훅
 * 개수가 달라져 리액트가 상태를 잘못 짝지운다.
 */
export type SessionState = {
  /** 아직 판단 중이다. 이 동안 로그인으로 보내면 안 된다. */
  isLoading: boolean;
  isAuthenticated: boolean;
  /** 로그인한 사용자의 ID. 통합 인증의 {@code sub} 와 같은 값이다. */
  userId?: string;
};

export function useSession(): SessionState {
  // 조건 없이 호출한다(위 '훅 규칙' 참조). BFF 를 켜면 결과를 쓰지 않을 뿐이다.
  const auth: AuthContextProps = useAuth();

  const [bffSession, setBffSession] = useState<SessionState>({
    isLoading: true,
    isAuthenticated: false,
  });

  useEffect(() => {
    if (!BFF_ENABLED) {
      return;
    }

    let cancelled = false;
    fetchBffSession().then((session) => {
      if (cancelled) {
        return;
      }
      setBffSession({
        isLoading: false,
        isAuthenticated: session !== undefined,
        userId: session?.userId,
      });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (DESIGN_PREVIEW) {
    return { isLoading: false, isAuthenticated: true, userId: PREVIEW_USER_ID };
  }

  if (BFF_ENABLED) {
    return bffSession;
  }

  return {
    isLoading: auth.isLoading,
    // 오류가 났으면 로그인된 것으로 보지 않는다. 기존 가드가 auth.error 를 따로
    // 확인하던 것을 여기로 모은다.
    isAuthenticated: auth.isAuthenticated && !auth.error,
    userId: auth.user?.profile.sub,
  };
}

/**
 * 로그인을 시작한다.
 *
 * <p>보통은 <b>브라우저가 이동</b>하므로 호출한 뒤의 코드는 실행되지 않는다.
 *
 * <p><b>다만 이동하지 않는 경우가 있다.</b> 로그인이 방금 실패해서 돌아온 상황에서는
 * 다시 보내지 않는다(무한 반복 방지). 호출자는 그 경우를 화면에 반영해야 한다 —
 * 그러지 않으면 사용자가 아무 안내 없는 빈 화면에 남는다.
 *
 * @return 실제로 이동시켰으면 true
 */
export function startLogin(auth: AuthContextProps): boolean {
  if (BFF_ENABLED) {
    return bffLogin();
  }
  auth.signinRedirect().then(
    () => {},
    () => {}
  );
  return true;
}

/**
 * 로그아웃한다.
 *
 * <p>기존 방식은 세 단계를 순서대로 밟아야 한다 — 저장된 사용자 제거 → 인증 서버
 * 로그아웃 → 남은 상태 정리. 네 곳에 같은 코드가 흩어져 있던 것을 여기로 모은다.
 *
 * <p>BFF 에서는 게이트웨이 세션 하나만 끊으면 된다. 브라우저에 토큰이 없으므로 지울
 * 것이 없다.
 */
export async function startLogout(auth: AuthContextProps): Promise<void> {
  if (BFF_ENABLED) {
    await bffLogout();
    return;
  }

  await auth.removeUser();
  try {
    await auth.signoutRedirect({ id_token_hint: auth.user?.id_token });
  } finally {
    await auth.clearStaleState();
  }
}
